<template>
  <base-modal
    v-if="visible"
    hide-footer
    :title="$t('orders.add_geometry_from_observations')"
    @close="resolve(null)"
  >
      <div @click.stop="">
        <div class="row">
          <span>{{ $t('orders.new_geometry_name') }}</span>
        </div>
          <input
              id="name"
              v-model="geometry.name"
              type="text"
              class="form-control form-control-sm"
          />

        <div class="row flex items-center">
          <span class="mr-2 h-min">
            {{ $t('orders.new_geometry_color') }}
          </span>
          <color-picker
            :no-red-color="true"
            :default-color="geometry.color"
            @select-color="(color) => geometry.color = color"
          />
        </div>

        <div class="row mt-2">
          <span>{{ $t('observation_filter.observation_type') }}</span>
        </div>
          <select
              v-model="observationType"
              class="form-select form-select-sm"
              :disabled="!observationTypes"
          >
              <option v-for="option in observationTypeOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
              </option>
          </select>

        <div>
          <hr>
          <div class="col-2 mt-2">
            <span>{{ $t('orders.geometry_properties') }}</span>
          </div>
          <order-geometry-property-editor
            ref="orderGeometryPropertyEditor"
            :new-properties="geometry.geometryProperties"
            @add-property="addProperty"
            @remove-property="removeProperty"
          />
        </div>
        <div class="button-container pr-1">
          <button class="btn btn-secondary form-button" @click.stop="resolve(null)">
            {{ $t('common.cancel') }}
          </button>
          <button class="btn btn-success form-button" :disabled="!geometry.name || !observationType" @click.stop="resolve(geometry)">
            {{ $t('orders.add_geometry_from_observations_short') }}
          </button>
        </div>
      </div>
  </base-modal>
</template>

<script>
import {restApi} from "../mixins/RestApiMixin";
import OrderGeometryPropertyEditor from "../order/OrderGeometryPropertyEditor.vue";
import ColorPicker from "../ColorPicker.vue";
import {geomTypes} from "../mixins/ObservationMixin";
import {geometryMixin} from "../mixins/GeometryMixin";
import BaseModal from "@/components/BaseModal";

const defaultGeometry = (importedItemIndex) => ({
    name: "",
    label: null,
    geometry: null,
    color: "#20c997",
    type: null,
    geometryProperties: ["info"],
    calculate: false,
    visibility: true,
    lineWidth: 3,
    addPolylineEnds: false,
    importedItemIndex
})

export default {
    name: 'ObservationToRoadGeometry',
    components: {BaseModal, ColorPicker, OrderGeometryPropertyEditor},
    mixins: [restApi, geometryMixin],
    data() {
        return {
            geometry: defaultGeometry(),
            observationType: null,
            observationTypes: null,
            resolve: null,
            visible: false
        }
    },
    computed: {
        observationTypeOptions() {
            if (!this.observationTypes) {
                return [{ value: null, text: this.$t('common.loading') }]
            }
            return [
                { value: null, text: this.$t('observations.select_observation_type') },
                ...this.observationTypes.map(type => ({
                    value: type, text: type.name
                }))
            ]
        }
    },
    methods: {
        addProperty(newProperty) {
            this.geometry.geometryProperties.push(newProperty)
        },
        removeProperty(index) {
            if(this.geometry.geometryProperties.length > 0) {
                this.geometry.geometryProperties.splice(index, 1)
            }
        },
        createRoadGeometryFrom(observations, importedItemIndex) {
            if (this.resolve != null) {
                this.resolve(null)
            }
            this.reset(importedItemIndex)
            this.visible = true

            // Fetch observation types in background
            this.getObservationTypes(observations)
                .then(types => this.observationTypes = types)

            return new Promise(resolve => {
                this.resolve = (roadGeometry) => {
                    if (roadGeometry != null) {
                        resolve(this.addObservationFeatures(roadGeometry, observations))
                    } else {
                        resolve(null)
                    }
                    this.visible = false
                }
            })
        },
        reset(importedItemIndex) {
            this.geometry = defaultGeometry(importedItemIndex)
            this.observationType = null
            this.observationTypes = null
            this.resolve = null
        },
        async getObservationTypes(observations) {
            let typeIds = observations.map(o => o.type_id)
                .filter((v, i, a) => a.indexOf(v) === i) // Filter to unique ids
                .sort((a, b) => a - b) // Sort as numbers

            const types = await this.fetchObservationTypes()
            return typeIds.map(id => types.find(e => e.id === id))
        },
        async fetchObservationTypes() {
            const response = await this.axios.get(this.observationTypeUrl)
            return response.data
        },
        geometryTypeIdToString(typeId) {
            return {
                [geomTypes.POINT]: 'Point',
                [geomTypes.LINESTRING]: 'LineString',
            }[typeId]
        },
        coordinateToArray(coord) {
            return [coord.x, coord.y]
        },
        addObservationFeatures(roadGeometry, observations) {
            const typeId = this.observationType.id
            const type = this.geometryTypeIdToString(this.observationType.geometry_type)
            return {
                ...roadGeometry,
                type,
                geometry: {
                    features: observations
                        .filter(o => o.type_id === typeId)
                        .map((observation, index) => {
                            const { geometry } = observation
                            const baseProperties = {
                                ...Object.fromEntries(roadGeometry.geometryProperties.map(prop => ([prop, ""]))),
                                id: index,
                                importedItemIndex: this.geometry.importedItemIndex,
                            }

                            if (observation.info && observation.info.trim() !== '') {
                                baseProperties.info = observation.info
                            }

                            return {
                                geometry: type === 'Point' ? {
                                    bbox: this.getPointBbox(this.coordinateToArray(geometry.point)),
                                    coordinates: this.coordinateToArray(geometry.point),
                                    type,
                                } : {
                                    bbox: this.getPolylineBbox(geometry.line_string.points),
                                    coordinates: geometry.line_string.points,
                                    type,
                                },
                                properties: baseProperties,
                                type: 'Feature'
                            }
                        }),
                    type: 'FeatureCollection'
                }
            }
        },
    }
}
</script>
